"use client"

import { useEffect, useMemo, useRef, useState } from "react"

import { useInfiniteQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query"
import { useTranslations } from "next-intl"

import { PdpReviewSummary } from "@/components/marketplace"
import { REVIEWS_ANCHOR } from "@/components/marketplace/pdp/review-summary"
import type { ReviewSortKey } from "@/components/marketplace/pdp/review-summary"
import { Button, Text } from "@/components/ui"
import type { StoreProductReviews } from "@/platform/api/store-contract"
import { getProductReviewPage } from "@/platform/catalog/products"

const PAGE_SIZE = 4

interface PdpReviewsClientProps {
  productId: string
  renderedAt: number
  initialPage: StoreProductReviews | null
}

export const PdpReviewsClient = ({ productId, initialPage, renderedAt }: PdpReviewsClientProps) => {
  const t = useTranslations("pdp")
  const queryClient = useQueryClient()
  const focusAfterInitialRetry = useRef(false)
  const [sort, setSort] = useState<ReviewSortKey>("HIGHEST")
  const [rating, setRating] = useState(0)
  const query = useInfiniteQuery({
    queryKey: ["product-reviews", productId, sort, rating],
    initialPageParam: 0,
    initialData: initialPage && sort === "HIGHEST" && rating === 0 ? { pages: [initialPage], pageParams: [0] } : undefined,
    queryFn: ({ pageParam }) => getProductReviewPage(productId, { limit: PAGE_SIZE, offset: pageParam, sort, ...(rating > 0 ? { rating } : {}) }),
    getNextPageParam: (last) => last.next_offset ?? undefined,
  })
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.reviews) ?? [], [query.data])

  const summary =
    query.data?.pages.at(-1)?.summary ??
    queryClient.getQueryData<InfiniteData<StoreProductReviews>>(["product-reviews", productId, "HIGHEST", 0])?.pages.at(-1)?.summary ??
    initialPage?.summary

  useEffect(() => {
    if (!summary || !focusAfterInitialRetry.current) return
    focusAfterInitialRetry.current = false
    document.getElementById(REVIEWS_ANCHOR)?.focus()
  }, [summary])

  if (!summary) {
    return (
      <div id={REVIEWS_ANCHOR} tabIndex={-1} className="scroll-mt-sticky-page outline-none">
        {query.isFetching && <Text role="status">{t("reviews.loading")}</Text>}
        {query.isError && (
          <div role="alert">
            <Text as="p">{t("reviews.loadFailed")}</Text>
            <Button
              variant="quiet"
              onClick={() => {
                focusAfterInitialRetry.current = true
                void query.refetch()
              }}
              disabled={query.isFetching}
            >
              {t("reviews.retry")}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <PdpReviewSummary
      renderedAt={renderedAt}
      summary={summary}
      items={items}
      sort={sort}
      rating={rating}
      hasMore={query.hasNextPage}
      loading={query.isFetching}
      loadError={query.isError}
      onSortChange={setSort}
      onFilterChange={setRating}
      onRetry={async () => {
        const result = await (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())
        return !result.isError
      }}
      onLoadMore={async () => {
        const result = await query.fetchNextPage()
        return !result.isError
      }}
    />
  )
}
